import React, { useState } from 'react';
import Header from '../../components/ui/Header';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import { Checkbox } from '../../components/ui/Checkbox';
import { supabase } from '../../lib/supabase';

import { trackNewsletterSubscription } from '../../utils/analyticsEvents';

const NewsletterSubscription = () => {
  const [formData, setFormData] = useState({
    email: '',
    firstName: '',
    lastName: '',
    company: '',
    jobTitle: '',
    interests: [],
    gdprConsent: false,
    marketingConsent: false
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);
  const [errors, setErrors] = useState({});

  const interestOptions = [
    { id: 'data-strategy', label: 'Data Strategy & Governance' },
    { id: 'ai-ml', label: 'AI & Machine Learning' },
    { id: 'analytics', label: 'Analytics & BI' },
    { id: 'compliance', label: 'GDPR & Compliance' },
    { id: 'fractional-cdo', label: 'Fractional CDO Services' },
    { id: 'case-studies', label: 'Case Studies & Success Stories' }
  ];

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    // Clear error when user starts typing
    if (errors?.[field]) {
      setErrors(prev => ({ ...prev, [field]: null }));
    }
  };

  const handleInterestToggle = (interestId) => {
    setFormData(prev => ({
      ...prev,
      interests: prev?.interests?.includes(interestId)
        ? prev?.interests?.filter(id => id !== interestId)
        : [...prev?.interests, interestId]
    }));
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData?.email?.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/?.test(formData?.email)) {
      newErrors.email = 'Please enter a valid email address';
    }

    if (!formData?.firstName?.trim()) {
      newErrors.firstName = 'First name is required';
    }

    if (!formData?.gdprConsent) {
      newErrors.gdprConsent = 'You must accept the privacy policy to subscribe';
    }

    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);
    
    try {
      // Save subscriber to database
      const { data: subscriber, error: dbError } = await supabase?.from('newsletter_subscribers')?.insert([{
          email: formData?.email?.toLowerCase()?.trim(),
          first_name: formData?.firstName?.trim(),
          last_name: formData?.lastName?.trim(),
          company: formData?.company?.trim(),
          job_title: formData?.jobTitle?.trim(),
          interests: formData?.interests,
          marketing_consent: formData?.marketingConsent,
          subscribed_at: new Date()?.toISOString(),
          status: 'active'
        }])?.select()?.single();

      if (dbError) {
        if (dbError?.code === '23505') {
          throw new Error('This email is already subscribed to our newsletter');
        }
        throw dbError;
      }

      // Send welcome email via Edge Function
      const supabaseUrl = import.meta.env?.VITE_SUPABASE_URL;
      const { data: { session } } = await supabase?.auth?.getSession();
      
      const emailPayload = {
        to: formData?.email,
        firstName: formData?.firstName,
        interests: formData?.interests
      };

      const response = await fetch(`${supabaseUrl}/functions/v1/send-newsletter-welcome`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': session ? `Bearer ${session?.access_token}` : ''
        },
        body: JSON.stringify(emailPayload)
      });

      let emailSent = false;
      if (!response?.ok) {
        const errorData = await response?.json()?.catch(() => ({}));
        console.error('Failed to send welcome email:', errorData);
        // Email failed but subscription was saved - this is acceptable
        emailSent = false;
      } else {
        emailSent = true;
      }

      // Track newsletter subscription
      trackNewsletterSubscription(formData?.interests);

      setSubmitStatus(emailSent ? 'success' : 'partial');
      setFormData({
        email: '',
        firstName: '',
        lastName: '',
        company: '',
        jobTitle: '',
        interests: [],
        gdprConsent: false,
        marketingConsent: false
      });
    } catch (error) {
      console.error('Newsletter subscription error:', error);
      setSubmitStatus('error');
      setErrors({ submit: error?.message || 'Failed to subscribe. Please try again.' });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-surface via-background to-surface">
      <Header />
      
      <main className="pt-24 pb-20">
        {/* Hero Section */}
        <section className="py-16 px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto text-center">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-secondary/10 rounded-full mb-6">
              <Icon name="Mail" size={32} className="text-secondary" />
            </div>
            <h1 className="text-4xl lg:text-5xl font-bold mb-6 bg-gradient-to-r from-primary via-secondary to-primary bg-clip-text text-transparent">
              Stay Ahead with Data Strategy Insights
            </h1>
            <p className="text-xl text-muted-foreground max-w-3xl mx-auto mb-8">
              Join 5,000+ data leaders receiving exclusive insights, case studies, and actionable strategies to transform their data capabilities.
            </p>
          </div>
        </section>

        {/* Benefits Section */}
        <section className="py-12 px-4 sm:px-6 lg:px-8 bg-card/50">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-2xl font-bold text-center mb-12">What You'll Receive</h2>
            <div className="grid md:grid-cols-3 gap-8">
              <div className="text-center">
                <div className="inline-flex items-center justify-center w-12 h-12 bg-primary/10 rounded-full mb-4">
                  <Icon name="TrendingUp" size={24} className="text-primary" />
                </div>
                <h3 className="text-lg font-semibold mb-2">Strategic Insights</h3>
                <p className="text-muted-foreground text-sm">
                  Weekly analysis of data trends, AI innovations, and governance best practices
                </p>
              </div>
              <div className="text-center">
                <div className="inline-flex items-center justify-center w-12 h-12 bg-secondary/10 rounded-full mb-4">
                  <Icon name="FileText" size={24} className="text-secondary" />
                </div>
                <h3 className="text-lg font-semibold mb-2">Exclusive Case Studies</h3>
                <p className="text-muted-foreground text-sm">
                  Real transformation stories with ROI metrics and implementation roadmaps
                </p>
              </div>
              <div className="text-center">
                <div className="inline-flex items-center justify-center w-12 h-12 bg-success/10 rounded-full mb-4">
                  <Icon name="Zap" size={24} className="text-success" />
                </div>
                <h3 className="text-lg font-semibold mb-2">Early Access</h3>
                <p className="text-muted-foreground text-sm">
                  Priority access to tools, webinars, and consultation slots
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Subscription Form */}
        <section className="py-16 px-4 sm:px-6 lg:px-8">
          <div className="max-w-2xl mx-auto">
            {submitStatus === 'success' ? (
              <div className="bg-card rounded-2xl shadow-elevation p-8 text-center">
                <div className="w-16 h-16 bg-success/10 rounded-full flex items-center justify-center mx-auto mb-6">
                  <Icon name="CheckCircle" size={32} className="text-success" />
                </div>
                <h3 className="text-2xl font-semibold text-primary mb-4">
                  Welcome to Northstar Insights!
                </h3>
                <p className="text-muted-foreground mb-6">
                  Thank you for subscribing. Check your inbox for a welcome email with exclusive resources.
                </p>
                <Button
                  variant="outline"
                  onClick={() => setSubmitStatus(null)}
                  iconName="ArrowLeft"
                >
                  Subscribe Another Email
                </Button>
              </div>
            ) : submitStatus === 'partial' ? (
              <div className="bg-card rounded-2xl shadow-elevation p-8 text-center">
                <div className="w-16 h-16 bg-warning/10 rounded-full flex items-center justify-center mx-auto mb-6">
                  <Icon name="AlertCircle" size={32} className="text-warning" />
                </div>
                <h3 className="text-2xl font-semibold text-primary mb-4">
                  Subscription Confirmed!
                </h3>
                <p className="text-muted-foreground mb-4">
                  Your subscription has been saved successfully. However, we couldn't send the welcome email at this time.
                </p>
                <p className="text-sm text-muted-foreground mb-6">
                  Don't worry - you'll receive our next newsletter as scheduled. If you don't receive any emails within a week, please contact our support team.
                </p>
                <Button
                  variant="outline"
                  onClick={() => setSubmitStatus(null)}
                  iconName="ArrowLeft"
                >
                  Subscribe Another Email
                </Button>
              </div>
            ) : (
              <div className="bg-card rounded-2xl shadow-elevation p-8">
                <h2 className="text-2xl font-bold mb-6 text-center">Subscribe to Our Newsletter</h2>
                
                <form onSubmit={handleSubmit} className="space-y-6">
                  {/* Email */}
                  <Input
                    type="email"
                    label="Email Address"
                    required
                    value={formData?.email}
                    onChange={(e) => handleInputChange('email', e?.target?.value)}
                    error={errors?.email}
                    placeholder="you@company.com"
                  />

                  {/* Name Fields */}
                  <div className="grid md:grid-cols-2 gap-4">
                    <Input
                      type="text"
                      label="First Name"
                      required
                      value={formData?.firstName}
                      onChange={(e) => handleInputChange('firstName', e?.target?.value)}
                      error={errors?.firstName}
                      placeholder="John"
                    />
                    <Input
                      type="text"
                      label="Last Name"
                      value={formData?.lastName}
                      onChange={(e) => handleInputChange('lastName', e?.target?.value)}
                      placeholder="Doe"
                    />
                  </div>

                  {/* Company & Job Title */}
                  <div className="grid md:grid-cols-2 gap-4">
                    <Input
                      type="text"
                      label="Company"
                      value={formData?.company}
                      onChange={(e) => handleInputChange('company', e?.target?.value)}
                      placeholder="Your Company"
                    />
                    <Input
                      type="text"
                      label="Job Title"
                      value={formData?.jobTitle}
                      onChange={(e) => handleInputChange('jobTitle', e?.target?.value)}
                      placeholder="Data Director"
                    />
                  </div>

                  {/* Interests */}
                  <div className="space-y-3">
                    <label className="text-sm font-medium">Content Interests (Optional)</label>
                    <div className="grid md:grid-cols-2 gap-3">
                      {interestOptions?.map((interest) => (
                        <Checkbox
                          key={interest?.id}
                          id={interest?.id}
                          checked={formData?.interests?.includes(interest?.id)}
                          onChange={() => handleInterestToggle(interest?.id)}
                          label={interest?.label}
                        />
                      ))}
                    </div>
                  </div>

                  {/* GDPR Consent */}
                  <div className="space-y-3 pt-4 border-t border-border">
                    <Checkbox
                      id="gdprConsent"
                      checked={formData?.gdprConsent}
                      onChange={(e) => handleInputChange('gdprConsent', e?.target?.checked)}
                      required
                      label="I agree to the Privacy Policy"
                      description="Required: We'll process your data according to GDPR regulations. View our Privacy Policy for details."
                      error={errors?.gdprConsent}
                    />
                    <Checkbox
                      id="marketingConsent"
                      checked={formData?.marketingConsent}
                      onChange={(e) => handleInputChange('marketingConsent', e?.target?.checked)}
                      label="Send me personalized content recommendations"
                      description="Optional: Receive tailored insights based on your interests and industry."
                    />
                  </div>

                  {/* Submit Error */}
                  {errors?.submit && (
                    <div className="bg-error/10 border border-error rounded-lg p-4 flex items-start space-x-3">
                      <Icon name="AlertCircle" size={20} className="text-error flex-shrink-0 mt-0.5" />
                      <p className="text-sm text-error">{errors?.submit}</p>
                    </div>
                  )}

                  {/* Submit Button */}
                  <Button
                    type="submit"
                    fullWidth
                    loading={isSubmitting}
                    iconName="Send"
                    size="lg"
                  >
                    {isSubmitting ? 'Subscribing...' : 'Subscribe Now'}
                  </Button>

                  <p className="text-xs text-muted-foreground text-center">
                    Unsubscribe anytime. We respect your inbox and privacy.
                  </p>
                </form>
              </div>
            )}
          </div>
        </section>

        {/* Social Proof */}
        <section className="py-12 px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto">
            <div className="bg-card rounded-xl p-8 text-center">
              <div className="flex justify-center items-center space-x-8 mb-6">
                <div>
                  <div className="text-3xl font-bold text-primary">5,000+</div>
                  <div className="text-sm text-muted-foreground">Subscribers</div>
                </div>
                <div className="h-12 w-px bg-border"></div>
                <div>
                  <div className="text-3xl font-bold text-secondary">98%</div>
                  <div className="text-sm text-muted-foreground">Open Rate</div>
                </div>
                <div className="h-12 w-px bg-border"></div>
                <div>
                  <div className="text-3xl font-bold text-success">Weekly</div>
                  <div className="text-sm text-muted-foreground">Insights</div>
                </div>
              </div>
              <p className="text-muted-foreground italic">
                "The most actionable data strategy newsletter I've read. Every issue delivers real value."
              </p>
              <p className="text-sm text-muted-foreground mt-2">— Sarah Chen, VP of Data at TechCorp</p>
            </div>
          </div>
        </section>
      </main>
    </div>
  );
};

export default NewsletterSubscription;